import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Divider, FormLabel, InputAdornment, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfile } from "../../redux/Slice/AuthSlice";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { updateEmployeeByUser } from "../../redux/Slice/employeeSlice";
import { clearMessage } from "../../redux/Slice/message";
import { toast } from "sonner";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { updateImage, uploadImage } from "../../redux/Slice/imageSlice";
import { setLoading } from "../../redux/Slice/loaderSlice";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

const ProfilePage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showSecPassword, setShowSecPassword] = useState(false);
  const [showconPassword, setShowconPassword] = useState(false);
  const [imageUploaded, setImageUploaded] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const { profile } = useSelector((state) => state.auth);
  const { error } = useSelector((state) => state.reset);
  const { message } = useSelector((state) => state.message);

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [profileImage, setProfileImage] = useState(profile?.imgUrl || "");
  const [imageFile, setImageFile] = useState(null);

  const { imageUrl } = useSelector((state) => state.image);

  useEffect(() => {
    if (profile) {
      formik.setValues({
        name: profile.name || "",
        phoneNumber: profile.phoneNumber || "",
        email: profile.email || "",
      });
      setProfileImage(profile?.imgUrl);
      setImageUploaded(false);
    }
  }, [profile]);

  useEffect(() => {
    if (imageUrl) {
      setProfileImage(imageUrl);
    }
  }, [imageUrl]);

  useEffect(() => {
    if (message?.message) {
      toast(message.message);
      dispatch(clearMessage());
    }
  }, [message, dispatch]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickNewShowPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowconPassword = () => setShowconPassword((show) => !show);

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = async (e) => {
    dispatch(setLoading(true));
  
    const file = e.target.files[0];
    if (!file) {
      dispatch(setLoading(false));
      return;
    }
  
    const reader = new FileReader();
    reader.onload = () => {
      setProfileImage(reader.result);
      setImageUploaded(true);
    };
    reader.readAsDataURL(file);
  
    const formData = new FormData();
    formData.append("image", file);
  
    try {
      if (profile?.imgUrl) {
        formData.append("oldKey", profile.imgUrl); 
        await dispatch(updateImage(formData)).unwrap();
      } else {
        await dispatch(uploadImage(formData)).unwrap();
      }
      toast.success("Image updated successfully!");
    } catch (error) {
      console.error("Error updating image:", error);
      toast.error("Failed to update image. Please try again.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleImageSubmit = () => {
    formik.handleSubmit();
    setImageUploaded(false);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phoneNumber: Yup.string()
      .matches(/^\d*$/, "Phone number must contain only digits")
      .required("phoneNumber is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      phoneNumber: "",
      email: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch(setLoading(true));
      const r_id = profile?._id;
      const data = {
        ...values,
        ...(imageUrl ? { imgUrl: imageUrl } : {}),
      };
      dispatch(updateEmployeeByUser({ r_id: r_id, formData: data }))
        .then(() => {
          dispatch(setLoading(false));
          setImageUploaded(false);
        })
        .catch((error) => {
          dispatch(setLoading(false));
        });
    },
  });

  const handlePasswordSubmit = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }
    // Password Change Logic
    const r_id = profile?._id;

    const data = {
      ...formik.values,
      ...(imageUrl ? { imgUrl: imageUrl } : {}),
      oldPassword: passwordData.oldPassword,
      newPassword: passwordData.newPassword,
      confirmPassword: passwordData.confirmPassword,
    };

    try {
      dispatch(setLoading(true));
      await dispatch(updateEmployeeByUser({ r_id: r_id, formData: data })).unwrap();
      dispatch(setLoading(false));
      toast.success("Password updated successfully!");
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error("Failed to update password. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 4,
      }}
    >
       <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 3,
          position: "relative",
        }}
      >
        <Avatar alt="Profile Picture" src={profileImage} sx={{ width: 140, height: 140 }} />
        {!imageUploaded && (
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            id="profile-image"
            onChange={handleImageChange}
          />
        )}
        <Button
          component={imageUploaded ? "button" : "label"}
          htmlFor={imageUploaded ? undefined : "profile-image"}
          onClick={imageUploaded ? handleImageSubmit : undefined}
          sx={{
            position: "absolute",
            bottom: 10,
            left: "45%",
            backgroundColor: "#2FB354",
            color: "white",
            borderRadius: "50%",
            "&:hover": { backgroundColor: "#2FB354" },
            width: 40,
            height: 40,
            minWidth: 40,
            padding: 0,
          }}
        >
          {imageUploaded ? <CheckIcon sx={{ fontSize: 18 }} /> : <EditIcon sx={{ fontSize: 18 }} />}
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ p: 3, border: "1px solid #ddd", borderRadius: 2 }}>
        <Grid item size={12} style={{ display: "flex", alignItems: "center" }}>
          <InfoIcon color="action" sx={{ mr: 2 }} />
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            Basic Information
          </Typography>
        </Grid>

        <Grid item size={12}>
          <Divider />
        </Grid>

        <Grid item size={12} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", flexBasis: "20%" }}>
            <FormLabel style={{ textAlign: "left", marginRight: "8px" }}>Full Name</FormLabel>
            {/* <HelpOutlineIcon fontSize="small" color="action" /> */}
          </div>
          <TextField
            style={{ flexBasis: "80%" }}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item size={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <FormLabel style={{ flexBasis: "20%", textAlign: "left" }} htmlFor="phone-number">
            Phone Number 
            {/* <span style={{ fontStyle: "italic", color: "gray" }}>(Optional)</span> */}
          </FormLabel>
          <TextField
            name="phoneNumber"
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
            helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
            style={{ flexBasis: "80%" }}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item size={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <FormLabel style={{ flexBasis: "20%", textAlign: "left" }}>Email Address</FormLabel>
          <TextField
            name="email"
            value={formik.values.email}
            style={{ flexBasis: "80%" }}
            fullWidth
            variant="outlined"
            inputProps={{
              readOnly: true,
            }}
            helperText="Please Contact the admin to change the email"
          />
        </Grid>

        {!showSecPassword && (
          <Grid item size={12} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="success" onClick={formik.handleSubmit}>
              Submit
            </Button>
          </Grid>
        )}
      </Grid>

      {showSecPassword && (
        <Grid container spacing={3} sx={{ p: 3, border: "1px solid #ddd", borderRadius: 2, mt: 5 }}>
          <Grid item size={12} style={{ display: "flex", alignItems: "center" }}>
            <LockIcon color="action" sx={{ mr: 2 }} />
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Change Password
            </Typography>
          </Grid>

          <Grid item size={12}>
            <Divider />
          </Grid>

          <Grid item size={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <FormLabel style={{ flexBasis: "20%", textAlign: "left" }}>Old Password</FormLabel>
            <TextField
              name="oldPassword"
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              style={{ flexBasis: "80%" }}
              fullWidth
              variant="outlined"
              placeholder="Enter Old Password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {showPassword ? (
                      <Visibility sx={{ cursor: "pointer" }} onClick={handleClickShowPassword} />
                    ) : (
                      <VisibilityOff sx={{ cursor: "pointer" }} onClick={handleClickShowPassword} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item size={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <FormLabel style={{ flexBasis: "20%", textAlign: "left" }}>New Password</FormLabel>
            <TextField
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              style={{ flexBasis: "80%" }}
              fullWidth
              variant="outlined"
              placeholder="Enter new Password"
              type={showNewPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {showNewPassword ? (
                      <Visibility sx={{ cursor: "pointer" }} onClick={handleClickNewShowPassword} />
                    ) : (
                      <VisibilityOff sx={{ cursor: "pointer" }} onClick={handleClickNewShowPassword} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item size={12} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <FormLabel style={{ flexBasis: "20%", textAlign: "left" }}>Confirm Password</FormLabel>
            <TextField
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              style={{ flexBasis: "80%" }}
              fullWidth
              variant="outlined"
              placeholder="Confirm your Password"
              type={showconPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {showconPassword ? (
                      <Visibility sx={{ cursor: "pointer" }} onClick={handleClickShowconPassword} />
                    ) : (
                      <VisibilityOff sx={{ cursor: "pointer" }} onClick={handleClickShowconPassword} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item size={12} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="success" onClick={handlePasswordSubmit}>
              Save Changes
            </Button>
          </Grid>
          {error && (
            <Grid item size={12}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Toggle Button for Show/Hide Password Section */}
      <Button
        variant="text"
        color="primary"
        sx={{ mt: 3 }}
        onClick={() => setShowSecPassword(!showSecPassword)}
      >
        {showSecPassword ? "Hide Password Section" : "Show Password Section"}
      </Button>
    </Box>
  );
};

export default ProfilePage;